import { Swiper, SwiperSlide } from 'swiper/react';
import {
 Autoplay,
 Navigation,
 Pagination,
 Scrollbar,
 A11y,
} from 'swiper/modules';
import './productdisplay.scss';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { useEffect, useState } from 'react';
import LoaderBtn from '../LoaderBtn/LoaderBtn';
import {
 OrderDetails,
 ProductDetailProps,
} from '../../interface/interfaces';
import RenderProperty from '../RenderProductProp/RenderProperty';
import { useNotification } from '../../context/NotificationContext';
import { useCart } from '../../context/CartContext';
import { useNavigate, useParams } from 'react-router-dom';
import cloneDeep from 'lodash/cloneDeep';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { addToWishlist } from '../../api/requests';
import { CallToBuyModal } from '../CallToBuyModal/CallToBuyModal';

type Props = {
 details: ProductDetailProps | null;
};

const ProductDisplay = ({ details }: Props) => {
 const { isLiked } = useParams();
 const { addToCart } = useCart();
 const { notify } = useNotification();
 const navigate = useNavigate();
 const { isLoggedIn } = useAuth();

 const [orderDetails, setOrderDetails] = useState<OrderDetails>({
  id: '',
  productId: '',
  productName: '',
  imgUrl: '',
  quantity: 1,
  properties: [],
  selectedProperties: [],
  totalAmount: 0,
  isAvailable: false,
  maxQuantity: 0,
  priceData: {
   currency_symbol: '$',
   current_price: 0,
  },
 });

 const [wishListData, setWishListData] = useState({
  isLiked: false,
  isLiking: false,
 });

 const [status, setStatus] = useState({
  isSaved: false,
  isLoading: false,
  btnText: 'Add to cart',
 });

 const [isCallModalOpen, setIsCallModalOpen] = useState(false);

 useEffect(() => {
  if (details) {
   const orderId = Math.random().toString(36).substring(2, 12);
   const clonedDetails = cloneDeep(details);

   setOrderDetails({
    id: orderId,
    productId: clonedDetails.productId,
    productName: clonedDetails.name,
    imgUrl: clonedDetails.imgUrl,
    quantity: 1,
    properties: clonedDetails.properties,
    selectedProperties: [],
    totalAmount: clonedDetails.price_data.current_price as number,
    priceData: clonedDetails.price_data,
    isAvailable: clonedDetails.isAvailable,
    maxQuantity: clonedDetails.availableQuantity,
   });
  }
 }, [details]);

 useEffect(() => {
  if (isLiked && isLiked === 'liked') {
   setWishListData((prev) => {
    return {
     ...prev,
     isLiked: true,
    };
   });
  }
 }, [isLiked]);

 const handleQuantity = (value: number, max: number) => {
  if (!orderDetails) {
   return;
  }

  let newQ = orderDetails.quantity + value;

  if (newQ < 1) {
   return;
  }

  if (newQ > max) {
   notify.error('Maximum quantity reached!');
   return;
  }

  let amount = (
   (details?.price_data.current_price as number) * newQ
  ).toFixed(2);

  setOrderDetails((prev) => {
   return {
    ...prev,
    quantity: newQ,
    totalAmount: parseFloat(amount),
   };
  });
 };

 // const handleUpdateCart=async ()=>{
 //    try {

 //     setStatus((prev)=>{
 //         return {
 //          ...prev,
 //          isLoading:true,
 //          btnText:'Updating...',
 //          isNewUpdate:true
 //         }
 //      })

 //    let resp= await updateOrder(orderDetails)

 //    if(resp){
 //      setStatus((prev)=>{
 //         return {
 //          ...prev,
 //          isLoading:false,
 //          btnText:'Continue shopping',
 //          isNewUpdate:false
 //         }
 //      })

 //      notify.success('Updated cart!')
 //    }

 //    } catch (error) {
 //     throw new Error('An error occured while updating cart,please try again!')
 //    }
 // }

 const handleAddToCart = async () => {
  try {
   setStatus((prev) => {
    return {
     ...prev,
     isLoading: true,
     btnText: 'Saving...',
    };
   });

   let resp = await addToCart(orderDetails);

   if (resp) {
    setStatus((prev) => {
     return {
      ...prev,
      isLoading: false,
      btnText: 'Continue shopping',
      isSaved: true,
     };
    });

    if (details) {
     const newId = Math.random().toString(36).substring(2, 12);
     const cloned = cloneDeep(details);

     //Trying to reset order details with default data
     setOrderDetails({
      id: newId,
      productId: cloned.productId,
      productName: cloned.name,
      imgUrl: cloned.imgUrl,
      quantity: 1,
      properties: cloned.properties,
      selectedProperties: [],
      totalAmount: cloned.price_data.current_price as number,
      priceData: cloned.price_data,
      isAvailable: cloned.isAvailable,
      maxQuantity: cloned.availableQuantity,
     });
    }

    notify.success('Added to cart!');

    // setTimeout(() => {
    //     setStatus((prev) => ({
    //         ...prev,
    //         btnText: 'Add to cart',
    //         isSaved: false,
    //     }));
    // }, 7000);
   } else {
    setStatus((prev) => ({
     ...prev,
     btnText: 'Add to cart',
     isSaved: false,
     isLoading: false,
    }));
   }
  } catch (error) {
   throw new Error(
    'An error occured while saving cart,please try again!',
   );
  }
 };

 const handleCartFunc = async () => {
  if (status.isLoading) return;

  try {
   if (!status.isSaved) {
    await handleAddToCart();
   } else {
    navigate('/products');
   }
  } catch (error) {
   setStatus((prev) => ({
    ...prev,
    isLoading: false,
    btnText: 'Try Again',
   }));
   notify.error((error as Error).message);
  }
 };

 const setWishlistLoader = (value: boolean) => {
  setWishListData((prev) => {
   return {
    ...prev,
    isLiking: value,
   };
  });
 };

 const setWishlistLike = (value: boolean) => {
  setWishListData((prev) => {
   return {
    ...prev,
    isLiked: value,
   };
  });
 };

 const wishlistHandler = async () => {
  if (!isLoggedIn()) {
   notify.error('Sign in is required');
   return;
  }

  if (!wishListData.isLiked) {
   setWishlistLoader(true);
   addToWishlist(details!.productId).then(
    () => {
     setWishlistLoader(false);
     setWishlistLike(true);
     notify.success('Product added to wishlist');
    },
    (error: any) => {
     setWishlistLoader(false);
     notify.error(error.message);
    },
   );
  }
 };

 //   useEffect(() => {
 //     if(status.isLoading) return

 //     if (status.isSaved) {
 //       handleUpdateCart().catch(err => {
 //         setStatus(prev => ({
 //           ...prev,
 //           isLoading: false,
 //           btnText: 'Try Again',
 //         }));
 //         notify.error((err as Error).message);
 //       });
 //     }
 //     // eslint-disable-next-line
 //   }, [orderDetails]);
 return (
  <div className="productdisplay">
   {details && (
    <div className="dispGrid">
     <div className="slideshow">
      <Swiper
       modules={[Autoplay, Navigation, Pagination, Scrollbar, A11y]}
       spaceBetween={22}
       slidesPerView={1}
       navigation
       pagination={{ clickable: true }}
       breakpoints={{
        0: { spaceBetween: 20 },
        650: { spaceBetween: 22 },
       }}
      >
       <div
        className={`likebtn ${wishListData.isLiked ? 'liked' : ''}`}
        onClick={wishlistHandler}
       >
        <AnimatePresence mode="wait">
         {wishListData.isLiking && (
          <motion.div
           key="spinner"
           initial={{ opacity: 0, scale: 0.8 }}
           animate={{ opacity: 1, scale: 1 }}
           exit={{ opacity: 0, scale: 0.8 }}
           transition={{ duration: 0.2 }}
           className="smallspinner"
          ></motion.div>
         )}

         {!wishListData.isLiking && (
          <motion.svg
           key={wishListData.isLiked ? 'liked' : 'unliked'} // triggers exit/enter animation
           initial={{ scale: 0.8, opacity: 0 }}
           animate={{ scale: [1.5, 0.9, 1], opacity: 1 }}
           exit={{ scale: 0.6, opacity: 0 }}
           transition={{ duration: 0.3, ease: 'easeOut' }}
           width="24"
           height="24"
           viewBox="0 0 24 24"
           fill="none"
           xmlns="http://www.w3.org/2000/svg"
          >
           <path
            d="M16.5436 4C13.5005 4 12.0005 7 12.0005 7C12.0005 7 10.5005 4 7.45735 4C4.98423 4 3.02579 6.06906 3.00048 8.53797C2.94892 13.6628 7.06595 17.3073 11.5786 20.3702C11.703 20.4548 11.85 20.5001 12.0005 20.5001C12.151 20.5001 12.2979 20.4548 12.4224 20.3702C16.9345 17.3073 21.0516 13.6628 21.0005 8.53797C20.9752 6.06906 19.0167 4 16.5436 4Z"
            stroke="#B8B8B8"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
           />
          </motion.svg>
         )}
        </AnimatePresence>
       </div>

       {details.productImages.map((url, idx) => (
        <SwiperSlide key={idx}>
         <div className="imageBox">
          <img
           className="productImg"
           alt="product"
           src={url}
          ></img>
         </div>
        </SwiperSlide>
       ))}
      </Swiper>
     </div>

     <div className="productinfo">
      <div className="nameNprice">
       <div className="productName">{details.name}</div>
       <div className="productPrice">
        {details.price_data.currency_symbol}
        {details.price_data.current_price.toLocaleString()}
       </div>
      </div>

      <div className="productProps">
       <div className="gridc">
        {/* <div className='eachprop'> */}

        <div className="title">Availability:</div>
        <div className="value">
         {details.isAvailable && (
          <span className="tval">Instock</span>
         )}
         {!details.isAvailable && (
          <span className="tval">Out of stock</span>
         )}
        </div>

        {/* </div> */}

        {/* <div className='eachprop'> */}

        <div className="title">Ratings:</div>
        <div className="value">
         <div className="productReception">
          <div className="stars">
           {[...Array(details.stars)].map((_, i) => (
            <div
             className="svgicon"
             key={i}
            >
             <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
             >
              <path
               d="M6.44828 3.57658C7.03668 2.11017 7.33088 1.37605 7.80929 1.27432C7.93498 1.24759 8.06488 1.24759 8.19056 1.27432C8.66806 1.37513 8.96318 2.10926 9.55157 3.57658C9.8861 4.41061 10.0529 4.82762 10.3663 5.11173C10.4549 5.19116 10.5497 5.26143 10.6505 5.32253C11.0125 5.54158 11.4643 5.5819 12.368 5.66255C13.8977 5.80003 14.662 5.86785 14.8957 6.30411C14.9441 6.39453 14.9769 6.49243 14.9929 6.59373C15.0708 7.08222 14.508 7.59272 13.3835 8.61646L13.0719 8.90058C12.5458 9.37899 12.2837 9.6182 12.1315 9.91607C12.0401 10.0952 11.9788 10.2881 11.9501 10.4871C11.9024 10.8179 11.9794 11.1653 12.1334 11.8591L12.1884 12.1065C12.4642 13.3511 12.6026 13.9734 12.4303 14.2786C12.3541 14.4138 12.2449 14.5275 12.1128 14.6089C11.9807 14.6903 11.8301 14.7369 11.6751 14.7442C11.325 14.7616 10.831 14.3593 9.84211 13.5537C9.19139 13.023 8.86603 12.7582 8.50492 12.6546C8.1749 12.56 7.82495 12.56 7.49493 12.6546C7.13383 12.7582 6.80755 13.023 6.15683 13.5537C5.16884 14.3584 4.67484 14.7616 4.32382 14.7451C4.16888 14.7376 4.01841 14.6908 3.88649 14.6092C3.75456 14.5276 3.64551 14.4139 3.56953 14.2786C3.39723 13.9734 3.53562 13.3511 3.81149 12.1065L3.86648 11.8591C4.02045 11.1653 4.09744 10.8179 4.04978 10.4871C4.02102 10.2881 3.95971 10.0952 3.86831 9.91607C3.71617 9.6182 3.45405 9.37899 2.92797 8.90058L2.61636 8.61646C1.49181 7.59364 0.929989 7.08222 1.00698 6.59373C1.02293 6.49243 1.05577 6.39453 1.10413 6.30411C1.33783 5.86785 2.10312 5.80003 3.63185 5.66255C4.53553 5.5819 4.98737 5.54158 5.34847 5.32345C5.44979 5.26222 5.54494 5.19131 5.63259 5.11173C5.94695 4.82762 6.11375 4.41061 6.44828 3.57658Z"
               fill="#FFC453"
               stroke="#FFC453"
               stroke-width="1.83301"
              />
             </svg>
            </div>
           ))}

           {/* {[...Array((4 - data.stars))].map((_, i) => (<div className='svgicon stripped' key={i}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.44828 3.57658C7.03668 2.11017 7.33088 1.37605 7.80929 1.27432C7.93498 1.24759 8.06488 1.24759 8.19056 1.27432C8.66806 1.37513 8.96318 2.10926 9.55157 3.57658C9.8861 4.41061 10.0529 4.82762 10.3663 5.11173C10.4549 5.19116 10.5497 5.26143 10.6505 5.32253C11.0125 5.54158 11.4643 5.5819 12.368 5.66255C13.8977 5.80003 14.662 5.86785 14.8957 6.30411C14.9441 6.39453 14.9769 6.49243 14.9929 6.59373C15.0708 7.08222 14.508 7.59272 13.3835 8.61646L13.0719 8.90058C12.5458 9.37899 12.2837 9.6182 12.1315 9.91607C12.0401 10.0952 11.9788 10.2881 11.9501 10.4871C11.9024 10.8179 11.9794 11.1653 12.1334 11.8591L12.1884 12.1065C12.4642 13.3511 12.6026 13.9734 12.4303 14.2786C12.3541 14.4138 12.2449 14.5275 12.1128 14.6089C11.9807 14.6903 11.8301 14.7369 11.6751 14.7442C11.325 14.7616 10.831 14.3593 9.84211 13.5537C9.19139 13.023 8.86603 12.7582 8.50492 12.6546C8.1749 12.56 7.82495 12.56 7.49493 12.6546C7.13383 12.7582 6.80755 13.023 6.15683 13.5537C5.16884 14.3584 4.67484 14.7616 4.32382 14.7451C4.16888 14.7376 4.01841 14.6908 3.88649 14.6092C3.75456 14.5276 3.64551 14.4139 3.56953 14.2786C3.39723 13.9734 3.53562 13.3511 3.81149 12.1065L3.86648 11.8591C4.02045 11.1653 4.09744 10.8179 4.04978 10.4871C4.02102 10.2881 3.95971 10.0952 3.86831 9.91607C3.71617 9.6182 3.45405 9.37899 2.92797 8.90058L2.61636 8.61646C1.49181 7.59364 0.929989 7.08222 1.00698 6.59373C1.02293 6.49243 1.05577 6.39453 1.10413 6.30411C1.33783 5.86785 2.10312 5.80003 3.63185 5.66255C4.53553 5.5819 4.98737 5.54158 5.34847 5.32345C5.44979 5.26222 5.54494 5.19131 5.63259 5.11173C5.94695 4.82762 6.11375 4.41061 6.44828 3.57658Z" fill="#FFC453" stroke="#FFC453" stroke-width="1.83301" />
                    </svg>
                  </div>))} */}
          </div>

          <div className="reviews">
           ({details.reviews} review{details.reviews > 1 ? 's' : ''})
          </div>
          {/* {data.reviews >= 1 && (<div className='reviews'>({data.reviews} reviews)</div>)} */}
         </div>
        </div>
        {/* </div> */}

        {/* <div className='eachprop'> */}

        {!details?.vendor.is_call_to_buy_enabled && (
         <>
          <div className="title">Quantity:</div>
          <div className="value">
           <div className="quantitybox">
            <button
             className="q_btn"
             onClick={() =>
              handleQuantity(-1, details.availableQuantity | 0)
             }
            >
             -
            </button>
            <div className="q_val">{orderDetails?.quantity}</div>
            <button
             className="q_btn"
             onClick={() =>
              handleQuantity(+1, details.availableQuantity)
             }
            >
             +
            </button>
           </div>
          </div>
         </>
        )}

        {/* </div> */}

        {/* <div className='eachprop'> */}
        <div className="title">Total:</div>
        <div className="value">
         <div className="quantitybox">
          <div className="q_amount">
           {details.price_data.currency_symbol}
           {orderDetails?.totalAmount?.toLocaleString()}
          </div>
         </div>
        </div>
        {/* </div> */}

        {orderDetails?.properties?.map((eachProp, index) => (
         <RenderProperty
          prop={eachProp}
          propId={index}
          setOrder={setOrderDetails}
          key={index}
         ></RenderProperty>
        ))}

        {/* vendor name */}
        <div className="title">Sold by:</div>
        <div className="value">
         <div className="quantitybox">
          <div className="">
           {details.vendor.business_name ||
            `${details?.vendor?.user_first_name} ${
             details?.vendor?.user_last_name
            }`}
          </div>
         </div>
        </div>

        {/* vebdor address */}
        <div className="title">Vendor Address:</div>
        <div className="value">
         <div className="quantitybox">
          <div className="">{details.vendor.address || 'NA'}</div>
         </div>
        </div>
       </div>
      </div>

      <div className="btn_pack">
       {!details?.vendor?.is_call_to_buy_enabled ? (
        <div className="btn_pack">
         <div>
          <LoaderBtn
           className={`actbtn full ${details.isAvailable ? '' : 'disabled'}`}
           text={status.btnText}
           clickFunction={handleCartFunc}
           thickness={3}
           isLoading={status.isLoading}
           disabled={!details.isAvailable}
          />
         </div>
         <NavLink to={'/cart'}>
          <LoaderBtn
           className={`actbtn open ${details.isAvailable ? '' : 'disabled'}`}
           text={'Check out'}
           thickness={3}
           spinnerBackColor="var(--primary-two)"
           spinnerForeColor="var(--primary)"
           isLoading={false}
           disabled={!details.isAvailable}
          />
         </NavLink>
        </div>
       ) : (
        <div className="btn_pack">
         <LoaderBtn
          className={`actbtn full ${details.isAvailable ? '' : 'disabled'}`}
          text={'Call to Buy'}
          clickFunction={() => setIsCallModalOpen(true)}
          thickness={3}
          isLoading={false}
          disabled={!details.isAvailable}
         />
        </div>
       )}
      </div>
     </div>
    </div>
   )}
   <CallToBuyModal
    isOpen={isCallModalOpen}
    onClose={() => setIsCallModalOpen(false)}
    phoneNumber={details?.vendor?.telephone_no || ''}
    vendorName={
     details?.vendor?.business_name ||
     `${details?.vendor?.user_first_name} ${details?.vendor?.user_last_name}`
    }
   />
  </div>
 );
};

export default ProductDisplay;
